import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Trophy, Dumbbell, Plus, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Schedule = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [connections, setConnections] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadCalendarEvents();
    loadConnections();
  }, [filterType]);

  const loadCalendarEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('event_type', filterType);
      }
      
      const response = await fetch(`${backendUrl}/api/calendar/events?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      } else {
        toast({
          title: "Error",
          description: "Failed to load calendar events",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast({
        title: "Error",
        description: "Failed to load calendar events",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/calendar/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCalendarEvents();
    setRefreshing(false);
    toast({
      title: "Success",
      description: "Calendar events refreshed",
    });
  };

  const connectCalendar = async (provider) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/calendar/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          provider: provider,
          provider_email: `mock.${provider}@example.com`
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: result.message,
        });
        await loadConnections();
        await loadCalendarEvents();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to connect calendar",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast({
        title: "Error",
        description: "Failed to connect calendar",
        variant: "destructive",
      });
    }
  };

  const formatEventTime = (startDateTime, endDateTime) => {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    
    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    };
    
    return {
      date: formatDate(start),
      time: `${formatTime(start)} - ${formatTime(end)}`,
      fullDate: start
    };
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'practice':
        return <Dumbbell className="w-4 h-4" />;
      case 'game':
        return <Trophy className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getEventBadgeVariant = (eventType) => {
    switch (eventType) {
      case 'practice':
        return 'secondary';
      case 'game':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = new Date(event.start_datetime).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    
    // Sort dates
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));
    const sortedGrouped = {};
    sortedDates.forEach(date => {
      sortedGrouped[date] = grouped[date].sort((a, b) => 
        new Date(a.start_datetime) - new Date(b.start_datetime)
      );
    });
    
    return sortedGrouped;
  };

  const groupedEvents = groupEventsByDate(events);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
        <span className="ml-2 text-gray-600">Loading schedule...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Schedule</h1>
          <p className="text-gray-600">View your upcoming practices, games, and events</p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Calendar Connections */}
      {connections.length === 0 && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-800">
              <Calendar className="w-5 h-5 mr-2" />
              Connect Your Calendars
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-700 mb-4">
              Connect your Google or Apple Calendar to see all your practices, games, and events in one place.
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={() => connectCalendar('google')} 
                variant="outline"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Connect Google Calendar
              </Button>
              <Button 
                onClick={() => connectCalendar('apple')} 
                variant="outline"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Connect Apple Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connected Calendars Info */}
      {connections.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Connected Calendars ({connections.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {connections.map((connection, index) => (
                <Badge key={index} variant="outline" className="flex items-center">
                  {connection.provider === 'google' ? '📧' : '🍎'} 
                  <span className="ml-1">{connection.provider_email}</span>
                </Badge>
              ))}
              <Button 
                onClick={() => connectCalendar('google')} 
                variant="ghost"
                size="sm"
                className="ml-2"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Controls */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="practice">Practices</SelectItem>
              <SelectItem value="game">Games</SelectItem>
              <SelectItem value="other">Other Events</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <span className="text-sm text-gray-500">
          {events.length} event{events.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Events List */}
      {Object.keys(groupedEvents).length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600 mb-4">
              {connections.length === 0 
                ? "Connect your calendar to see your upcoming events."
                : "No events match your current filter criteria."
              }
            </p>
            {connections.length === 0 && (
              <div className="flex gap-2 justify-center">
                <Button onClick={() => connectCalendar('google')} variant="outline">
                  Connect Google Calendar
                </Button>
                <Button onClick={() => connectCalendar('apple')} variant="outline">
                  Connect Apple Calendar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEvents).map(([date, dayEvents]) => (
            <div key={date}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                {new Date(date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h2>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {dayEvents.map((event) => {
                  const timeInfo = formatEventTime(event.start_datetime, event.end_datetime);
                  
                  return (
                    <Card key={event.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg flex items-center">
                            {getEventIcon(event.event_type)}
                            <span className="ml-2 truncate">{event.title}</span>
                          </CardTitle>
                          <Badge variant={getEventBadgeVariant(event.event_type)}>
                            {event.event_type}
                          </Badge>
                        </div>
                        {event.opponent && (
                          <p className="text-sm font-medium text-orange-600">
                            vs {event.opponent}
                          </p>
                        )}
                      </CardHeader>
                      
                      <CardContent className="space-y-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 mr-2" />
                          {timeInfo.time}
                        </div>
                        
                        {event.location && (
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-2" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                        
                        {event.team_roster && event.team_roster.length > 0 && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            {event.team_roster.length} team member{event.team_roster.length !== 1 ? 's' : ''}
                          </div>
                        )}
                        
                        {event.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                        
                        {event.notes && (
                          <div className="bg-blue-50 p-2 rounded text-sm text-blue-800">
                            <strong>Note:</strong> {event.notes}
                          </div>
                        )}
                        
                        {event.reminders && event.reminders.length > 0 && (
                          <div className="text-xs text-gray-500">
                            {event.reminders.length} reminder{event.reminders.length !== 1 ? 's' : ''} set
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Schedule;